import React, { useState, useRef, useEffect } from "react";
import fileAttachmant from "../../../../assets/file_attachment.svg";
import upArrow from "../../../../assets/up-arrow.svg";
import "./AICreatedFlashcard.css";
import { useSelector } from "react-redux";
import { getAIGeneratedFlashcards } from "../../../../services/DashboardApi";
import ChatGPTLoader from "../ai-content/ChatGPTLoader";
import fileIcon from "../../../../assets/file-icon.svg";
import { logger } from "../../../../utils/logger";
import { FaArrowLeft } from "react-icons/fa";

const AICreatedFlashcard = ({
  topicId,
  subtopicId,
  notebookId,
  onFlashcardSaved,
  setShowFlashcardForm,
  setAICreated,
  setFlashcardFormData,
  boxId,
  setShowCreateOptions,
  ai_content_data,
}) => {
  // Extract AI_PROMPT and AI_RESPONSE into separate arrays of objects
  const aiPromptsAndResponses = ai_content_data.map((item) => ({
    prompt: item.AI_PROMPT,
    response: item.AI_RESPONSE,
  }));

  console.log("Extracted Prompts and Responses: ", aiPromptsAndResponses);
  const aiContentConversation = {
    role: "user",
    content: aiPromptsAndResponses,
  };
  const { userId } = useSelector((state) => state.userDetails);

  const [userPrompt, setUserPrompt] = useState("");
  const [pendingPrompt, setPendingPrompt] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState({});
  const fileInputRef = useRef(null);
  const [aiGeneratedFlashcards, setAIGeneratedFlashcards] = useState([]);
  const selectedSubTopicDetails = useSelector(
    (state) => state.selectedSubTopic.selectedSubTopicData
  );
  const selectedMainTab = useSelector((state) => state.mainTab.tab_name);
  let subtopic_name = selectedSubTopicDetails.subtopicName;

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);

    files.forEach((file) => {
      if (file) {
        const allowedTypes = [
          "application/pdf",
          "text/plain",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "image/jpeg",
          "image/png",
          "image/gif",
          "image/webp",
        ];
        const maxSize = 5 * 1024 * 1024; // 5MB

        if (!allowedTypes.includes(file.type)) {
          alert(
            "Please select a PDF, TXT, DOC, or image file (JPEG, PNG, GIF, WEBP)"
          );
          return;
        }

        if (file.size > maxSize) {
          alert("File size should be less than 5MB");
          return;
        }

        // Generate unique ID for the file
        const fileId = Date.now() + Math.random().toString(36).substr(2, 9);

        setSelectedFiles((prev) => [...prev, { id: fileId, file }]);

        // Create preview URL for images
        if (file.type.startsWith("image/")) {
          const url = URL.createObjectURL(file);
          setPreviewUrls((prev) => ({ ...prev, [fileId]: url }));
        }
      }
    });
  };

  const clearFile = (fileId) => {
    setSelectedFiles((prev) => prev.filter((f) => f.id !== fileId));
    if (previewUrls[fileId]) {
      URL.revokeObjectURL(previewUrls[fileId]);
      setPreviewUrls((prev) => {
        const newUrls = { ...prev };
        delete newUrls[fileId];
        return newUrls;
      });
    }
  };

  // Cleanup preview URLs when component unmounts
  useEffect(() => {
    return () => {
      Object.values(previewUrls).forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  useEffect(() => {
    if (subtopic_name) {
      setUserPrompt(`Generate 5 flashcards for ${subtopic_name}`);
    }
  }, [subtopic_name]);

  const handleSendMessage = async () => {
    let topicName = selectedSubTopicDetails.topicName;
    let subtopicName = selectedSubTopicDetails.subtopicName;
    let prompt = userPrompt.trim();
    logger.log("Prompt: ", prompt);
    if (!prompt) return;

    try {
      setLoading(true);
      setUserPrompt("");
      setPendingPrompt(prompt);

      const formData = new FormData();
      formData.append("userPrompt", prompt);
      formData.append("topicId", topicId);
      formData.append("subtopicId", subtopicId);
      formData.append("notebookId", notebookId);
      formData.append("userId", userId);
      formData.append(
        "aiContentConversation",
        JSON.stringify(aiContentConversation)
      );
      formData.append("section", selectedMainTab);
      formData.append("topicName", topicName);
      formData.append("subtopicName", subtopicName);

      selectedFiles.forEach(({ file }, index) => {
        formData.append(`document${index}`, file);
      });

      let aiResponse = await getAIGeneratedFlashcards(formData);
      if (!aiResponse.success) {
        throw new Error(aiResponse.error || "Failed to get AI response");
      } else {
        aiResponse = aiResponse.data.result;
        console.log("Response for AI Generated Flashcards: ", aiResponse);
        setFlashcardFormData(aiResponse);
        logger.log("Response from AI: ", aiResponse);
        setAIGeneratedFlashcards(aiResponse);
        setShowFlashcardForm(true);
        setAICreated(false);
        setPendingPrompt(null);
        setSelectedFiles([]);
      }
    } catch (error) {
      logger.log("Error handling message:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilePaste = (event) => {
    const items = event.clipboardData.items;
    const files = [];

    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === "file") {
        const file = items[i].getAsFile();
        if (file && file.type.startsWith("image/")) {
          const defaultName = `pasted_image_${Date.now()}.${
            file.type.split("/")[1]
          }`;
          const renamedFile = new File([file], defaultName, {
            type: file.type,
          });
          files.push(renamedFile);
        } else {
          files.push(file);
        }
      }
    }

    if (files.length > 0) {
      handleFileSelect({ target: { files } });
    }
  };

  const handleBackClick = () => {
    setShowCreateOptions(true);
    setAICreated(false);
    setShowFlashcardForm(false);
    setPendingPrompt(null);
    setSelectedFiles([]);
  };

  useEffect(() => {
    const messageBox = document.querySelector(
      `.message-box[data-box-id="${boxId}"] .message-ai`
    );
    messageBox.addEventListener("paste", handleFilePaste);

    return () => {
      messageBox.removeEventListener("paste", handleFilePaste);
    };
  }, [boxId]);

  return (
    <React.Fragment>
      <div
        style={{
          width: "100%",
          textAlign: "left",
          padding: "10px",
          marginLeft: "20%",
          marginTop: "2%",
        }}
      >
        <FaArrowLeft className="back-icon" onClick={handleBackClick} />
      </div>
      <div className="ai-content-messages">
        {aiGeneratedFlashcards.map((ai_content, index, ATTACHMENTS) => (
          <div key={index} className="ai-content-text">
            <br />
          </div>
        ))}

        {/* {pendingPrompt && ( */}
        <div className="ai-content-text">
          <br />
          <h3>{pendingPrompt}</h3>
          {isLoading && <ChatGPTLoader />}
        </div>
        {/* )} */}
      </div>

      <div className="message-box" data-box-id={boxId}>
        <input
          className="message-ai"
          placeholder="Message AI"
          value={userPrompt}
          onChange={(e) => setUserPrompt(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
        />
        <div className="attach-wrapper">
          <div className="left-icons">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              style={{ display: "none" }}
              accept=".pdf,.txt,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp"
              multiple
            />
            <span
              className="attach-icon"
              onClick={() => fileInputRef.current.click()}
            >
              <img src={fileAttachmant} alt="File Attachment Icon" />
              {selectedFiles.length > 0 && (
                <span className="file-badge">{selectedFiles.length}</span>
              )}
            </span>
          </div>
          <span className="send-icon" onClick={handleSendMessage}>
            <img src={upArrow} alt="Send Icon" />
          </span>
        </div>
        {selectedFiles.length > 0 && (
          <div className="selected-files">
            {selectedFiles.map(({ id, file }) => (
              <div key={id} className="file-info">
                <div className="file-details">
                  <img src={fileIcon} alt="File" className="file-icon" />
                  <span>{file.name}</span>
                </div>
                <button onClick={() => clearFile(id)} className="remove-file">
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default AICreatedFlashcard;
